import { Integration_Auth_Names_Enum } from '@main/graphql/types.generated';
import type { Promisable } from '@main/shared/types';
import type { Request } from 'express';
import z from 'zod/v4';

import type { ManagedTokenProvider } from '../backend/managed-credentials';
import type { ConnectorConfigSchemas } from './connector-config';
import type {
  IntegrationHandlerConfig,
  IntegrationOAuth2Config,
} from './integration-handler-config';

export enum IntegrationAuthType {
  App = 'app',
  Manual = 'manual',
  Oauth2Code = 'oauth2_code',
}

export const CALLBACK_PATH = 'integrations/auth/callback';

export enum CallbackFailure {
  ConnectorNameConflict = 'connectorNameConflict',
}

export type IntegrationAuth<
  TName extends Integration_Auth_Names_Enum = Integration_Auth_Names_Enum,
  TConfigSchema extends z.ZodObject = z.ZodObject,
  TEncryptedConfigSchema extends z.ZodObject = z.ZodObject,
> =
  | IntegrationAuthManual<TName, TConfigSchema, TEncryptedConfigSchema>
  | IntegrationAuthApp<TName, TConfigSchema, TEncryptedConfigSchema>
  | IntegrationAuthOAuth2Code<TName, TConfigSchema, TEncryptedConfigSchema>;

export interface IntegrationAuthManual<
  TName extends Integration_Auth_Names_Enum = Integration_Auth_Names_Enum,
  TConfigSchema extends z.ZodObject = z.ZodObject,
  TEncryptedConfigSchema extends z.ZodObject = z.ZodObject,
> {
  readonly ['_']: {
    Config: z.infer<TConfigSchema> & z.infer<TEncryptedConfigSchema>;
    PlainConfig: z.infer<TConfigSchema>;
    EncryptedConfig: z.infer<TEncryptedConfigSchema>;
    NamedConfig: z.infer<TConfigSchema> & z.infer<TEncryptedConfigSchema> & { authName: TName };
  };

  type: IntegrationAuthType.Manual;
  name: TName;
  configSchema: TConfigSchema;
  encryptedConfigSchema: TEncryptedConfigSchema;
  userConfigSchema: ConnectorConfigSchemas;

  deprecated?: boolean;
}

export interface IntegrationAuthApp<
  TName extends Integration_Auth_Names_Enum = Integration_Auth_Names_Enum,
  TConfigSchema extends z.ZodObject = z.ZodObject,
  TEncryptedConfigSchema extends z.ZodObject = z.ZodObject,
> {
  readonly ['_']: {
    Config: z.infer<TConfigSchema> & z.infer<TEncryptedConfigSchema>;
    PlainConfig: z.infer<TConfigSchema>;
    EncryptedConfig: z.infer<TEncryptedConfigSchema>;
    NamedConfig: z.infer<TConfigSchema> & z.infer<TEncryptedConfigSchema> & { authName: TName };
  };

  type: IntegrationAuthType.App;
  name: TName;
  clientId: (config: IntegrationHandlerConfig) => string;
  installUrl: string;
  scope: string;
  configSchema: TConfigSchema;
  encryptedConfigSchema: TEncryptedConfigSchema;
  userConfigSchema: ConnectorConfigSchemas;
  getConfigFromCallback: (req: Request) => Promisable<{
    config?: Partial<z.infer<TConfigSchema>>;
    encryptedConfig?: Partial<z.infer<TEncryptedConfigSchema>>;
  }>;

  deprecated?: boolean;
}

export enum TokenAuthMethod {
  ClientSecretPost = 'client_secret_post',
  ClientSecretBasic = 'client_secret_basic',
}

export type TokenRejectionCode =
  | 'invalid_grant'
  | 'invalid_client'
  | 'rate_limited'
  | 'server_error'
  | 'temporarily_unavailable';

export type TokenRejectionClassifier = (response: {
  readonly status: number;
  readonly headers: Pick<Headers, 'get'>;
  readonly body: string;
}) => TokenRejectionCode | undefined;

export interface IntegrationAuthOAuth2Code<
  TName extends Integration_Auth_Names_Enum = Integration_Auth_Names_Enum,
  TConfigSchema extends z.ZodObject = z.ZodObject,
  TEncryptedConfigSchema extends z.ZodObject = z.ZodObject,
> {
  readonly ['_']: {
    Config: z.infer<TConfigSchema> & z.infer<TEncryptedConfigSchema>;
    PlainConfig: z.infer<TConfigSchema>;
    EncryptedConfig: z.infer<TEncryptedConfigSchema>;
    NamedConfig: z.infer<TConfigSchema> &
      z.infer<TEncryptedConfigSchema> &
      ManagedTokenProvider & { authName: TName };
  };

  type: IntegrationAuthType.Oauth2Code;
  name: TName;
  clientId: (
    config: IntegrationOAuth2Config,
    connectorConfig: z.infer<TConfigSchema> & z.infer<TEncryptedConfigSchema>,
  ) => string;
  clientSecret: (
    config: IntegrationOAuth2Config,
    connectorConfig: z.infer<TConfigSchema> & z.infer<TEncryptedConfigSchema>,
  ) => string;
  authorizeUrl: (config: z.infer<TConfigSchema> & z.infer<TEncryptedConfigSchema>) => string;
  tokenUrl: (config: z.infer<TConfigSchema> & z.infer<TEncryptedConfigSchema>) => string;
  scope: string;
  tokenAuthMethod?: TokenAuthMethod;
  pkce?: boolean;
  extraTokenParams?: Record<string, string>;
  classifyTokenRejection?: TokenRejectionClassifier;
  configSchema: TConfigSchema;
  encryptedConfigSchema: TEncryptedConfigSchema;
  userConfigSchema: ConnectorConfigSchemas;

  deprecated?: boolean;
}

export interface IntegrationAuthGroup<TGroup extends IntegrationAuth[] = IntegrationAuth[]> {
  readonly ['_']: {
    Config: TGroup[number]['_']['Config'];
    NamedConfig: TGroup[number]['_']['NamedConfig'];
  };

  group: TGroup;
}

export function isEligible(
  auth: Pick<IntegrationAuth, 'name' | 'deprecated'>,
  currentAuthName?: string | null,
): boolean {
  return auth.deprecated !== true || auth.name === currentAuthName;
}

export function group<TGroup extends IntegrationAuth[]>(group: TGroup) {
  return { group } as IntegrationAuthGroup<TGroup>;
}

type IntegrationAuthFactoryInput<
  TAuth extends ConnectorConfigSchemas & {
    userConfigSchema: ConnectorConfigSchemas;
  },
> = Omit<TAuth, '_' | 'type' | 'userConfigSchema'> & {
  userConfigSchema?: ConnectorConfigSchemas;
};

function withAuthConfigDefaults<
  TInput extends ConnectorConfigSchemas & {
    userConfigSchema?: ConnectorConfigSchemas;
  },
>(input: TInput) {
  return {
    ...input,
    userConfigSchema: input.userConfigSchema ?? {
      configSchema: input.configSchema,
      encryptedConfigSchema: input.encryptedConfigSchema,
    },
  };
}

export function manual<
  TName extends Integration_Auth_Names_Enum,
  TConfigSchema extends z.ZodObject,
  TEncryptedConfigSchema extends z.ZodObject,
>(
  input: IntegrationAuthFactoryInput<
    IntegrationAuthManual<TName, TConfigSchema, TEncryptedConfigSchema>
  >,
) {
  return {
    type: IntegrationAuthType.Manual,
    ...withAuthConfigDefaults(input),
  } as unknown as IntegrationAuthManual<TName, TConfigSchema, TEncryptedConfigSchema>;
}

export function app<
  TName extends Integration_Auth_Names_Enum,
  TConfigSchema extends z.ZodObject,
  TEncryptedConfigSchema extends z.ZodObject,
>(
  input: IntegrationAuthFactoryInput<
    IntegrationAuthApp<TName, TConfigSchema, TEncryptedConfigSchema>
  >,
) {
  return {
    type: IntegrationAuthType.App,
    ...withAuthConfigDefaults(input),
  } as unknown as IntegrationAuthApp<TName, TConfigSchema, TEncryptedConfigSchema>;
}

export function oauth2Code<
  TName extends Integration_Auth_Names_Enum,
  TConfigSchema extends z.ZodObject,
  TEncryptedConfigSchema extends z.ZodObject,
>(
  input: IntegrationAuthFactoryInput<
    IntegrationAuthOAuth2Code<TName, TConfigSchema, TEncryptedConfigSchema>
  >,
) {
  return {
    type: IntegrationAuthType.Oauth2Code,
    ...withAuthConfigDefaults(input),
  } as unknown as IntegrationAuthOAuth2Code<TName, TConfigSchema, TEncryptedConfigSchema>;
}
